import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkAdminCredentials, setAdminSession } from '@/lib/admin';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!account.trim() || !password.trim()) {
      setFormError('请输入账号和密码');
      return;
    }
    setIsSubmitting(true);
    setFormError('');

    const ok = await checkAdminCredentials(account.trim(), password);
    if (ok) {
      setAdminSession();
      navigate('/admin/dashboard', { replace: true });
      return;
    }

    setIsSubmitting(false);
    setFormError('账号或密码错误，请重试');
  };

  const canSubmit = account.trim().length > 0 && password.length > 0;

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-brown-950 via-brown-900 to-brown-950 flex items-center justify-center px-4 relative overflow-hidden">
      {/* 装饰圆环 */}
      <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full border border-gold-400/10"></div>
      <div className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full border border-gold-400/10"></div>
      <div className="absolute top-1/3 -left-16 w-40 h-40 rounded-full bg-gold-500/5 blur-2xl"></div>

      <div className="relative w-full max-w-[400px]">
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-7 md:p-9 animate-fade-up">
          {/* 品牌 */}
          <div className="flex flex-col items-center mb-8">
            <div className="w-14 h-14 rounded-xl bg-gold-500 flex items-center justify-center mb-4">
              <span className="w-7 h-7 flex items-center justify-center text-brown-950">
                <i className="ri-vip-crown-fill text-2xl"></i>
              </span>
            </div>
            <h1 className="text-white text-xl font-bold tracking-tight">金安中国 · 管理后台</h1>
            <p className="text-brown-400 text-xs mt-1.5 tracking-widest">ADMIN CONSOLE</p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
            {/* 账号 */}
            <div className="flex items-center border border-white/10 rounded-lg px-4 py-3 bg-white/5 focus-within:border-gold-400/60 focus-within:ring-2 focus-within:ring-gold-400/10 transition-all">
              <span className="w-5 h-5 flex items-center justify-center text-brown-400 flex-shrink-0">
                <i className="ri-admin-line text-lg"></i>
              </span>
              <input
                type="text"
                autoComplete="username"
                value={account}
                onChange={(e) => {
                  setAccount(e.target.value);
                  if (formError) setFormError('');
                }}
                placeholder="请输入后台账号"
                className="ml-3 w-full bg-transparent text-sm text-white placeholder:text-brown-500 outline-none"
              />
            </div>

            {/* 密码 */}
            <div className="flex items-center border border-white/10 rounded-lg px-4 py-3 bg-white/5 focus-within:border-gold-400/60 focus-within:ring-2 focus-within:ring-gold-400/10 transition-all">
              <span className="w-5 h-5 flex items-center justify-center text-brown-400 flex-shrink-0">
                <i className="ri-lock-2-line text-lg"></i>
              </span>
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (formError) setFormError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canSubmit && !isSubmitting) {
                    handleSubmit(e as unknown as React.FormEvent);
                  }
                }}
                placeholder="请输入后台密码"
                className="ml-3 w-full bg-transparent text-sm text-white placeholder:text-brown-500 outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="ml-2 w-5 h-5 flex items-center justify-center text-brown-400 hover:text-brown-200 transition-colors cursor-pointer flex-shrink-0"
                tabIndex={-1}
              >
                <i className={`${showPassword ? 'ri-eye-line' : 'ri-eye-off-line'} text-lg`}></i>
              </button>
            </div>

            {/* 错误提示 */}
            {formError && (
              <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5">
                <span className="w-4 h-4 flex items-center justify-center text-red-400 flex-shrink-0">
                  <i className="ri-error-warning-line text-sm"></i>
                </span>
                <span className="text-xs text-red-300">{formError}</span>
              </div>
            )}

            {/* 登录按钮 */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`mt-1 w-full py-3 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                canSubmit && !isSubmitting
                  ? 'bg-gold-500 hover:bg-gold-600 text-brown-950 shadow-lg shadow-gold-500/20'
                  : 'bg-white/10 text-brown-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? '登录中...' : '登 录'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => navigate('/home')}
              className="inline-flex items-center gap-1.5 text-brown-400 text-xs hover:text-gold-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-sm"></i>
              </span>
              返回首页
            </button>
          </div>
        </div>

        <p className="text-center text-brown-600 text-xs mt-6">金安中国运营管理平台 · 仅限授权人员使用</p>
      </div>
    </div>
  );
}